import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  type IdentityApi,
  useApi,
  configApiRef,
} from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  type CatalogApi,
} from '@backstage/plugin-catalog-react';
import { isUserEntity } from '@backstage/catalog-model';
import { useCallback } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { ENVIRONMENT_PRODUCTION } from '@internal/backstage-plugin-neo4j-app-common';

type SendPlatformxEventResult =
  | { success: true; error?: undefined }
  | { success: false; error: Error };

const getUserEmail = async (
  identityApi: IdentityApi,
  catalogApi: CatalogApi,
): Promise<string> => {
  const profile = await identityApi.getProfileInfo();
  if (profile.email) {
    return profile.email;
  }

  const identity = await identityApi.getBackstageIdentity();
  const userEntity = await catalogApi.getEntityByRef(identity.userEntityRef);

  if (
    userEntity &&
    isUserEntity(userEntity) &&
    userEntity.spec.profile?.email
  ) {
    return userEntity.spec.profile.email;
  }

  throw new Error('Could not determine user email.');
};

export function useSendPlatformxEvent():
  | ((event: string) => Promise<SendPlatformxEventResult>)
  | undefined {
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const environment = configApi.getOptionalString('custom.environment');
  const isProduction = environment === ENVIRONMENT_PRODUCTION;

  const {
    loading: emailLoading,
    error: emailError,
    value: emailValue,
  } = useAsync(
    async () =>
      isProduction ? getUserEmail(identityApi, catalogApi) : undefined,
    [identityApi, catalogApi],
  );

  const {
    loading: urlLoading,
    error: urlError,
    value: urlValue,
  } = useAsync(
    async () =>
      isProduction ? discoveryApi.getBaseUrl('neo4j-platformx') : undefined,
    [discoveryApi],
  );

  const sendPlatformxEvent = useCallback(
    async (eventId: string): Promise<SendPlatformxEventResult> => {
      try {
        const response = await fetchApi.fetch(
          `${urlValue}/send-platformx-event`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: emailValue,
              eventId,
            }),
          },
        );

        if (!response.ok) {
          const text = await response.text().catch(() => 'Unknown error');
          throw new Error(`Failed to send PlatformX event: ${text}`);
        }

        return { success: true };
      } catch (error) {
        const BASE_ERROR_MESSAGE = 'Failed to send PlatformX event';

        return {
          success: false,
          error: error instanceof Error ? error : new Error(BASE_ERROR_MESSAGE),
        };
      }
    },
    [fetchApi, emailValue, urlValue],
  );

  // Not ready to send events yet
  if (emailLoading || urlLoading) {
    return undefined;
  }

  if (emailError) {
    // eslint-disable-next-line no-console
    console.error(`Failed to get user email for PlatformX: ${emailError}`);
    return undefined;
  }

  if (urlError) {
    // eslint-disable-next-line no-console
    console.error(`Failed to get base URL for PlatformX: ${urlError}`);
    return undefined;
  }

  // If no errors, this should eventually be set
  if (!urlValue) {
    return undefined;
  }

  // If no errors, this should eventually be set
  if (!emailValue) {
    return undefined;
  }

  return sendPlatformxEvent;
}
